<script setup>
import ExportIcon from "@/components/Icons/ExportIcon.vue";
import MapUploadPanel from "@/components/MapPage/MapUploadPanel.vue";
import { ref } from "vue";
import { useMapUpload } from "@/composables/useMapUpload";
import { t } from "@/controllerLK/AvalLocalization";
import { defineEmits } from "vue";

const emit = defineEmits([
  "getProjectMapFiles",
  "set-map-loading",
  "map-created"
]);

// Используем composable для логики работы с картами
const { mapList, mapSearchString, searchMap, createMap, uploadMap } = useMapUpload();

// Состояние drag and drop
const isDragging = ref(false);

// Обработка создания карты
const handleCreateMap = async (map) => {
  await createMap(map, {
    onStart: () => emit("set-map-loading", true),
    onSuccess: (mapData) => {
      emit("set-map-loading", false);
      emit("map-created", mapData);
    },
    onError: () => emit("set-map-loading", false)
  });
};

// Обработка загрузки файла
const handleFileUpload = async (file) => {
  await uploadMap(file, {
    onStart: () => emit("set-map-loading", true),
    onSuccess: (mapData) => {
      emit("set-map-loading", false);
      emit("map-created", mapData);
    },
    onError: () => emit("set-map-loading", false)
  });
};

// Обработчик выбора файла через input
const handleFileInputChange = async (e) => {
  const files = e.target?.files;
  if (files && files.length > 0) {
    await handleFileUpload(files[0]);
    // Сброс значения input для возможности повторной загрузки того же файла
    e.target.value = "";
  }
};

// Drag and Drop обработчики
const handleDragEnter = (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDragging.value = true;
};

const handleDragOver = (e) => {
  e.preventDefault();
  e.stopPropagation();
};

const handleDragLeave = (e) => {
  e.preventDefault();
  e.stopPropagation();

  if (e.target.classList.contains("no-map")) {
    isDragging.value = false;
  }
};

const handleDrop = async (e) => {
  e.preventDefault();
  e.stopPropagation();
  isDragging.value = false;

  const files = e.dataTransfer?.files;
  if (files && files.length > 0) {
    await handleFileUpload(files[0]);
  }
};
</script>

<template>
  <div
    class="no-map"
    :class="{ 'no-map--drop': isDragging }"
    @dragenter="handleDragEnter"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <span class="no-map__title">
      {{ t("no_map_view.title") }}
    </span>
    <span class="no-map__subtitle">
      {{ t("no_map_view.subtitle") }}
      <br>
      {{ t("no_map_view.subtitle_format") }}
    </span>

    <MapUploadPanel
      :model-value="mapSearchString"
      :items="mapList"
      :placeholder="t('no_map_view.placeholder')"
      :with-file-upload="true"
      @update:model-value="searchMap"
      @add-item="handleCreateMap"
      @file-selected="handleFileInputChange"
    >
      <template #action-button="{ onClick }">
        <ButtonAccentSecondary
          v-tooltip:bottom="t('no_map_view.upload_own_map')"
          @click="onClick"
        >
          <ExportIcon />
        </ButtonAccentSecondary>
      </template>
    </MapUploadPanel>
  </div>
</template>

<style scoped lang="scss">
.no-map {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  gap: 20px;
  border-radius: 10px;
  height: 89.5vh;

  &--drop {
    border: 1px dashed var(--border-bd-on);
    background-color: var(--purple-purple10);
  }

  & > * {
    width: 360px;
  }

  &__title {
    font-size: 24px;
    font-weight: 500;
    line-height: 26px;
    color: var(--text-text-main);
    text-align: center;
  }

  &__subtitle {
    font-size: 14px;
    font-weight: 400;
    color: var(--text-text-main);
    text-align: center;
    padding: 0 10px;
    line-height: 140%;
  }
}
</style>